import React, { useState, useEffect, useContext } from 'react'
import { 
    ChevronRight, Building2, Activity, Users, UserX, 
    Calendar, AlertTriangle, Loader2, AlertCircle,
    TrendingUp, Clock, Target
} from 'lucide-react';
import AuthContext from '../../../context/AuthContext';
import superAdminServices from '../../../services/superAdminServices';
import OrganizationsChart from './organisation/OrganizationChart';

export default function DashboardSuperAdminHome() {
    const { authTokens } = useContext(AuthContext);
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sortBy, setSortBy] = useState('users');

    useEffect(() => {
        fetchDashboardData();
    }, [sortBy]);

    const fetchDashboardData = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const data = await superAdminServices.getDashboardStatistics(authTokens.access, {
                sort_by: sortBy
            });
            setDashboardData(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const getActionTypeLabel = (actionType) => {
        const labels = {
            'create': 'Créé',
            'update': 'Modifié',
            'delete': 'Supprimé',
            'login': 'Connexion',
            'logout': 'Déconnexion',
            'role_assign': 'Rôle assigné',
            'role_remove': 'Rôle retiré',
            'status_change': 'Statut changé',
            'assign': 'Assigné',
            'accept': 'Accepté',
            'reject': 'Rejeté',
            'start': 'Démarré',
            'complete': 'Terminé',
            'close': 'Fermé',
            'reopen': 'Réouvert',
            'achieve': 'Accompli',
            'unachieve': 'Non accompli',
            'add_party': 'Partie ajoutée',
            'remove_party': 'Partie retirée',
            'release': 'Publié',
            'review': 'Révisé',
            'publish': 'Publié',
            'pause_publication': 'Publication suspendue',
            'confirm': 'Confirmé',
            'upload_attachment': 'Pièce jointe ajoutée',
            'delete_attachment': 'Pièce jointe supprimée'
        };
        return labels[actionType] || actionType;
    };

    const getModelTypeLabel = (modelType) => {
        const labels = {
            'User': 'Utilisateur',
            'Risk': 'Risque',
            'Activity': 'Activité',
            'Process': 'Processus',
            'ProcessTask': 'Tâche',
            'Organization': 'Organisation',
            'AuditProgram': 'Programme d\'audit',
            'AuditPlan': 'Plan d\'audit',
            'AuditSection': 'Section d\'audit',
            'AuditQuestion': 'Question d\'audit',
            'RecommendationAction': 'Action de recommandation'
        };
        return labels[modelType] || modelType;
    };

    const formatDateTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
                    <span className="ml-3 text-gray-600">Chargement du tableau de bord...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center">
                        <AlertCircle className="h-5 w-5 text-red-600" />
                        <span className="ml-2 text-sm text-red-700">{error}</span>
                    </div>
                </div>
            </div>
        );
    }

    const stats = [
        { 
            label: 'Organisations', 
            value: dashboardData?.basic_counts?.total_organizations || '0', 
            icon: Building2, 
            color: 'bg-blue-50 border-blue-200' 
        },
        { 
            label: 'Utilisateurs totaux', 
            value: dashboardData?.basic_counts?.total_users || '0', 
            icon: Users, 
            color: 'bg-green-50 border-green-200' 
        },
        { 
            label: 'Sessions actives', 
            value: dashboardData?.basic_counts?.active_sessions || '0', 
            icon: Activity, 
            color: 'bg-purple-50 border-purple-200' 
        },
        { 
            label: 'Orgs non abonnées', 
            value: dashboardData?.basic_counts?.unsubscribed_organizations || '0', 
            icon: UserX, 
            color: 'bg-red-50 border-red-200' 
        }
    ];
  return (
    <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
            
        {/* Section de bienvenue */}
        <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Bienvenue sur votre tableau de bord</h2>
            <p className="text-xs text-gray-500 mt-1">Statistiques globales du système et gestion des organisations</p>
        </div>

        {/* Grille de statistiques */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            {stats.map((stat, idx) => {
            const Icon = stat.icon;
            return (
                <div key={idx} className={`${stat.color} border rounded-lg p-4 hover:shadow-sm transition-shadow`}>
                <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-medium text-gray-600">{stat.label}</p>
                    <Icon className="h-4 w-4 text-gray-400" />
                </div>
                <p className="text-xl font-bold text-gray-900">{stat.value}</p>
                </div>
            );
            })}
        </div>

        {/* Grille de contenu - Top Organizations */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            
            {/* Liste des organisations */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-900">Top 5 organisations</h3>
                    <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-500">Trier par:</span>
                        <select 
                            value={sortBy} 
                            onChange={(e) => setSortBy(e.target.value)}
                            className="text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                            <option value="users">Utilisateurs</option>
                            <option value="processes">Processus</option>
                            <option value="risks">Risques</option>
                        </select>
                    </div>
                </div>
                <div className="space-y-2">
                    <div className="grid grid-cols-12 gap-3 px-3 py-2 text-xs font-medium text-gray-500 border-b border-gray-100">
                        <div className="col-span-3">Organisation</div>
                        <div className="col-span-2 text-center">Utilisateurs</div>
                        <div className="col-span-2 text-center">Processus</div>
                        <div className="col-span-2 text-center">Risques</div>
                        <div className="col-span-1 text-center">Audits</div>
                        <div className="col-span-2 text-right">Statut</div>
                    </div>
                    {dashboardData?.top_organizations?.organizations?.map((org, idx) => (
                        <div key={idx} className="grid grid-cols-12 gap-3 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 rounded transition-colors items-center">
                            <div className="col-span-3 font-medium">{org.name}</div>
                            <div className="col-span-2 text-center text-gray-600">{org.users_count}</div>
                            <div className="col-span-2 text-center text-gray-600">{org.processes_count}</div>
                            <div className="col-span-2 text-center text-gray-600">{org.risks_count}</div>
                            <div className="col-span-1 text-center text-gray-600">{org.audit_plans_count || 0}</div>
                            <div className="col-span-2 text-right">
                                <span className={`inline-block px-2 py-0.5 rounded text-xs ${
                                    org.is_subscribed 
                                        ? 'bg-green-100 text-green-700' 
                                        : 'bg-red-100 text-red-700'
                                }`}>
                                    {org.is_subscribed ? 'Abonné' : 'Non abonné'}
                                </span>
                            </div>
                        </div>
                    )) || (
                        <div className="text-center py-4 text-xs text-gray-500">
                            Aucune organisation trouvée
                        </div>
                    )}
                </div>
            </div>

            {/* Graphique des organisations */}
            
                   
            <OrganizationsChart dashboardData={dashboardData} sortBy={sortBy} />
        </div>

        {/* Grille de contenu - Bottom Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            
            {/* Activité récente */}
            <div className="lg:col-span-2 bg-white rounded-lg border border-gray-200 p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Activité récente du système</h3>
                <div className="space-y-2">
                    {dashboardData?.recent_history?.length > 0 ? (
                        dashboardData.recent_history.map((activity) => (
                            <div key={activity.id} className="flex items-center justify-between py-2 px-3 text-xs hover:bg-gray-50 rounded transition-colors border-b border-gray-100 last:border-0">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center mb-1">
                                        <span className="font-medium text-gray-900">{getModelTypeLabel(activity.model_type)}</span>
                                        <span className="mx-2 text-gray-400">•</span>
                                        <span className="text-gray-600">{getActionTypeLabel(activity.action_type)}</span>
                                    </div>
                                    <p className="text-gray-500 truncate">
                                        <span className="font-medium">{activity.target_name}</span>
                                        {activity.done_by && (
                                            <span> par {activity.done_by}</span>
                                        )}
                                    </p>
                                    {activity.description && (
                                        <p className="text-gray-400 text-xs mt-0.5 truncate">{activity.description}</p>
                                    )}
                                </div>
                                <span className="text-gray-400 ml-2 whitespace-nowrap">
                                    {formatDateTime(activity.action_date)}
                                </span>
                            </div>
                        ))
                    ) : (
                        <div className="text-center py-4 text-xs text-gray-500">
                            <Activity className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                            <p>Aucune activité récente</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Abonnements expirant bientôt */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center mb-3">
                    <AlertTriangle className="h-4 w-4 text-amber-600 mr-2" />
                    <h3 className="text-sm font-semibold text-gray-900">Abonnements expirant</h3>
                </div>
                <div className="space-y-2">
                    {dashboardData?.expiring_subscriptions?.length > 0 ? (
                        dashboardData.expiring_subscriptions.map((org, idx) => (
                            <div key={idx} className="p-3 bg-amber-50 border border-amber-200 rounded hover:bg-amber-100 transition-colors">
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-xs font-medium text-gray-900">{org.name}</span>
                                    <span className={`text-xs px-2 py-0.5 rounded ${
                                        org.days_remaining <= 7 
                                            ? 'bg-red-100 text-red-700' 
                                            : 'bg-amber-100 text-amber-700'
                                    }`}>
                                        {org.days_remaining}j restants
                                    </span>
                                </div>
                                <div className="flex items-center justify-between text-xs text-gray-600">
                                    <span>{org.users_count} utilisateurs</span>
                                    <div className="flex items-center">
                                        <Calendar className="h-3 w-3 mr-1" />
                                        <span>{new Date(org.end_subscription_date).toLocaleDateString('fr-FR')}</span>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="text-center py-4 text-xs text-gray-500">
                            <Clock className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                            <p>Aucun abonnement expirant</p>
                        </div>
                    )}
                </div>
            </div>
        </div>

    </div>
  )
}
